// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Collect shapes that have a text frame on a given slide. */
async function getTextShapes(slide, context) {
    const shapes = slide.shapes;
    shapes.load('items');
    await context.sync();
    const results = [];
    for (const shape of shapes.items) {
        try {
            const textFrame = shape.textFrame;
            textFrame.load('hasText');
            results.push({ shape, textFrame });
        }
        catch {
            // Shape doesn't support text frames — skip
        }
    }
    await context.sync();
    return results;
}
/** Load a slide by 1-based index with bounds checking. */
async function getSlideByIndex(context, slideIndex) {
    const slides = context.presentation.slides;
    slides.load('items');
    await context.sync();
    if (slideIndex < 1 || slideIndex > slides.items.length) {
        throw new Error(`Invalid slide index: ${slideIndex}. Presentation has ${slides.items.length} slides.`);
    }
    return slides.items[slideIndex - 1];
}
// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------
export const powerpointTools = [
    // -----------------------------------------------------------------------
    // Existing: Read tools
    // -----------------------------------------------------------------------
    {
        tool: {
            name: 'powerpoint_get_presentation_text',
            description: 'Get all text content from the current PowerPoint presentation',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const text = await PowerPoint.run(async (context) => {
                const slides = context.presentation.slides;
                slides.load('items');
                await context.sync();
                const textParts = [];
                for (let i = 0; i < slides.items.length; i++) {
                    const tfShapes = await getTextShapes(slides.items[i], context);
                    const textRanges = [];
                    for (const { textFrame } of tfShapes) {
                        if (textFrame.hasText) {
                            const tr = textFrame.textRange;
                            tr.load('text');
                            textRanges.push(tr);
                        }
                    }
                    await context.sync();
                    const texts = textRanges
                        .map((tr) => tr.text?.trim())
                        .filter(Boolean);
                    if (texts.length > 0) {
                        textParts.push(`--- Slide ${i + 1} ---\n${texts.join('\n')}`);
                    }
                }
                return textParts.length > 0
                    ? textParts.join('\n\n')
                    : 'No text content found in presentation';
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_slide_text',
            description: 'Get text content from a specific slide by index (1-based)',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const text = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const tfShapes = await getTextShapes(slide, context);
                const textRanges = [];
                for (const { textFrame } of tfShapes) {
                    if (textFrame.hasText) {
                        const tr = textFrame.textRange;
                        tr.load('text');
                        textRanges.push(tr);
                    }
                }
                await context.sync();
                const texts = textRanges.map((tr) => tr.text?.trim()).filter(Boolean);
                return texts.length > 0
                    ? texts.join('\n')
                    : `No text content found on slide ${slideIndex}`;
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_slide_count',
            description: 'Get the number of slides in the presentation',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const count = await PowerPoint.run(async (context) => {
                const slides = context.presentation.slides;
                slides.load('items');
                await context.sync();
                return slides.items.length;
            });
            return { content: [{ type: 'text', text: String(count) }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_speaker_notes',
            description: 'Get the speaker notes from a specific slide (requires PowerPointApi 1.5+)',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const text = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const slideAny = slide;
                if (!slideAny.notesSlide) {
                    return '(no speaker notes)';
                }
                const notesSlide = slideAny.notesSlide;
                const shapes = notesSlide.shapes;
                shapes.load('items,type');
                await context.sync();
                // Match set_speaker_notes: target the Body placeholder specifically
                const writableFrames = [];
                for (const shape of shapes.items) {
                    try {
                        const tf = shape.textFrame;
                        tf.load('hasText');
                        writableFrames.push({ tf, shapeType: shape.type });
                    }
                    catch {
                        // skip non-text shapes
                    }
                }
                await context.sync();
                const bodyFrame = writableFrames.find((f) => f.shapeType === 'Body') ??
                    writableFrames[1] ??
                    writableFrames[0];
                if (!bodyFrame?.tf.hasText) {
                    return '(no speaker notes)';
                }
                const tr = bodyFrame.tf.textRange;
                tr.load('text');
                await context.sync();
                const text = tr.text?.trim();
                return text || '(no speaker notes)';
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    // -----------------------------------------------------------------------
    // Existing: Write tools
    // -----------------------------------------------------------------------
    {
        tool: {
            name: 'powerpoint_set_shape_text',
            description: 'Set the text of a shape on a slide. Use powerpoint_get_slide_shapes to discover shape indices.',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    shape_index: {
                        type: 'number',
                        description: 'The 0-based index of the shape on the slide',
                    },
                    text: {
                        type: 'string',
                        description: 'The text to set on the shape',
                    },
                },
                required: ['slide_index', 'shape_index', 'text'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const shapeIndex = args.shape_index;
            const text = args.text;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                if (shapeIndex < 0 || shapeIndex >= shapes.items.length) {
                    throw new Error(`Shape index ${shapeIndex} out of range. Slide ${slideIndex} has ${shapes.items.length} shape(s).`);
                }
                const shape = shapes.items[shapeIndex];
                try {
                    shape.textFrame.textRange.text = text;
                }
                catch {
                    throw new Error(`Shape ${shapeIndex} on slide ${slideIndex} does not support text`);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set text on shape ${shapeIndex} of slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_add_slide',
            description: 'Add a new slide to the presentation, optionally setting title and body text',
            inputSchema: {
                type: 'object',
                properties: {
                    title: {
                        type: 'string',
                        description: 'Title text for the new slide (optional)',
                    },
                    body: {
                        type: 'string',
                        description: 'Body text for the new slide (optional)',
                    },
                },
            },
        },
        execute: async (args) => {
            const title = args.title;
            const body = args.body;
            const slideNumber = await PowerPoint.run(async (context) => {
                const slides = context.presentation.slides;
                slides.add();
                slides.load('items');
                await context.sync();
                const newSlide = slides.items[slides.items.length - 1];
                if (title || body) {
                    const tfShapes = await getTextShapes(newSlide, context);
                    // First text shape = title, second = body
                    if (tfShapes[0] && title) {
                        tfShapes[0].textFrame.textRange.text = title;
                    }
                    if (tfShapes[1] && body) {
                        tfShapes[1].textFrame.textRange.text = body;
                    }
                    await context.sync();
                }
                return slides.items.length;
            });
            return {
                content: [{ type: 'text', text: `Added slide ${slideNumber}` }],
            };
        },
    },
    // -----------------------------------------------------------------------
    // NEW Tier 1: Structural awareness & search
    // -----------------------------------------------------------------------
    {
        tool: {
            name: 'powerpoint_get_slide_shapes',
            description: 'Get a structured inventory of all shapes on a slide, including name, type, position, dimensions, and text preview',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const result = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items,id,name,type,left,top,width,height');
                await context.sync();
                const shapeInfos = [];
                for (let i = 0; i < shapes.items.length; i++) {
                    const shape = shapes.items[i];
                    const info = {
                        index: i,
                        id: shape.id,
                        name: shape.name,
                        type: shape.type,
                        left: shape.left,
                        top: shape.top,
                        width: shape.width,
                        height: shape.height,
                        hasText: false,
                        textPreview: null,
                    };
                    try {
                        const textFrame = shape.textFrame;
                        textFrame.load('hasText');
                        shapeInfos.push({ info, textFrame });
                    }
                    catch {
                        info.hasText = false;
                        shapeInfos.push({ info, textFrame: null });
                    }
                }
                await context.sync();
                // Load text for shapes that have it
                const textRangeMap = [];
                for (let i = 0; i < shapeInfos.length; i++) {
                    const { info, textFrame } = shapeInfos[i];
                    if (textFrame?.hasText) {
                        info.hasText = true;
                        const tr = textFrame.textRange;
                        tr.load('text');
                        textRangeMap.push({ idx: i, tr });
                    }
                }
                await context.sync();
                for (const { idx, tr } of textRangeMap) {
                    const full = tr.text ?? '';
                    shapeInfos[idx].info.textPreview =
                        full.length > 120 ? `${full.substring(0, 120)}…` : full;
                }
                return shapeInfos.map((s) => s.info);
            });
            return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_search_text',
            description: 'Search for text across all slides and shapes. Returns matching locations with slide index, shape index, and surrounding context.',
            inputSchema: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description: 'The text to search for (case-insensitive)',
                    },
                },
                required: ['query'],
            },
        },
        execute: async (args) => {
            const query = args.query.toLowerCase();
            const matches = await PowerPoint.run(async (context) => {
                const slides = context.presentation.slides;
                slides.load('items');
                await context.sync();
                const results = [];
                for (let si = 0; si < slides.items.length; si++) {
                    const shapes = slides.items[si].shapes;
                    shapes.load('items,name');
                    // Batch: load shapes for this slide
                    await context.sync();
                    // Batch: probe all text frames + load text in one sync
                    const textFrames = [];
                    for (let shi = 0; shi < shapes.items.length; shi++) {
                        try {
                            const tf = shapes.items[shi].textFrame;
                            tf.load('hasText,textRange/text');
                            textFrames.push({ shapeIdx: shi, name: shapes.items[shi].name, tf });
                        }
                        catch {
                            // skip
                        }
                    }
                    await context.sync();
                    for (const { shapeIdx, name, tf } of textFrames) {
                        if (!tf.hasText)
                            continue;
                        const text = tf.textRange.text ?? '';
                        const lower = text.toLowerCase();
                        let pos = lower.indexOf(query);
                        while (pos !== -1) {
                            const start = Math.max(0, pos - 30);
                            const end = Math.min(text.length, pos + query.length + 30);
                            results.push({
                                slideIndex: si + 1,
                                shapeIndex: shapeIdx,
                                shapeName: name,
                                matchOffset: pos,
                                context: `…${text.substring(start, end)}…`,
                            });
                            pos = lower.indexOf(query, pos + 1);
                        }
                    }
                }
                return results;
            });
            if (matches.length === 0) {
                return { content: [{ type: 'text', text: `No matches found for "${args.query}"` }] };
            }
            return {
                content: [
                    {
                        type: 'text',
                        text: `Found ${matches.length} match(es):\n${JSON.stringify(matches, null, 2)}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_replace_text',
            description: 'Find and replace text within a specific shape, or across all shapes on a slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    shape_index: {
                        type: 'number',
                        description: 'The 0-based index of the shape on the slide (if omitted, replaces in all shapes on the slide)',
                    },
                    old_text: {
                        type: 'string',
                        description: 'The text to find',
                    },
                    new_text: {
                        type: 'string',
                        description: 'The replacement text',
                    },
                },
                required: ['slide_index', 'old_text', 'new_text'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const shapeIndex = args.shape_index;
            const oldText = args.old_text;
            const newText = args.new_text;
            const replacements = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                let count = 0;
                const processShape = async (shape) => {
                    try {
                        const tf = shape.textFrame;
                        tf.load('hasText');
                        await context.sync();
                        if (!tf.hasText)
                            return;
                        const tr = tf.textRange;
                        tr.load('text');
                        await context.sync();
                        const current = tr.text ?? '';
                        if (current.includes(oldText)) {
                            tr.text = current.replaceAll(oldText, newText);
                            await context.sync();
                            count++;
                        }
                    }
                    catch {
                        // skip shapes without text frames
                    }
                };
                if (shapeIndex !== undefined) {
                    if (shapeIndex < 0 || shapeIndex >= shapes.items.length) {
                        throw new Error(`Shape index ${shapeIndex} out of range. Slide has ${shapes.items.length} shape(s).`);
                    }
                    await processShape(shapes.items[shapeIndex]);
                }
                else {
                    for (const shape of shapes.items) {
                        await processShape(shape);
                    }
                }
                return count;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: replacements > 0
                            ? `Replaced text in ${replacements} shape(s) on slide ${slideIndex}`
                            : `Text "${oldText}" not found on slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_selected_slides',
            description: 'Get the indices and IDs of the currently selected slides',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const result = await PowerPoint.run(async (context) => {
                const selected = context.presentation.getSelectedSlides();
                selected.load('items,id');
                await context.sync();
                // Map selected IDs to 1-based indices
                const allSlides = context.presentation.slides;
                allSlides.load('items,id');
                await context.sync();
                const idToIndex = new Map();
                allSlides.items.forEach((s, i) => idToIndex.set(s.id, i + 1));
                return selected.items.map((s) => ({
                    id: s.id,
                    slideIndex: idToIndex.get(s.id) ?? -1,
                }));
            });
            return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_format_shape_text',
            description: 'Apply font formatting to the text in a shape (bold, italic, underline, color, size)',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    shape_index: {
                        type: 'number',
                        description: 'The 0-based index of the shape on the slide',
                    },
                    bold: { type: 'boolean', description: 'Set bold formatting' },
                    italic: { type: 'boolean', description: 'Set italic formatting' },
                    underline: { type: 'boolean', description: 'Set underline formatting' },
                    color: { type: 'string', description: 'Font color (e.g. "#FF0000", "red")' },
                    size: { type: 'number', description: 'Font size in points' },
                },
                required: ['slide_index', 'shape_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const shapeIndex = args.shape_index;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                if (shapeIndex < 0 || shapeIndex >= shapes.items.length) {
                    throw new Error(`Shape index ${shapeIndex} out of range. Slide ${slideIndex} has ${shapes.items.length} shape(s).`);
                }
                let font;
                try {
                    font = shapes.items[shapeIndex].textFrame.textRange.font;
                }
                catch {
                    throw new Error(`Shape ${shapeIndex} on slide ${slideIndex} does not support text`);
                }
                if (args.bold !== undefined)
                    font.bold = args.bold;
                if (args.italic !== undefined)
                    font.italic = args.italic;
                if (args.underline !== undefined)
                    font.underline = args.underline
                        ? 'Single'
                        : 'None';
                if (args.color !== undefined)
                    font.color = args.color;
                if (args.size !== undefined)
                    font.size = args.size;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Formatted text on shape ${shapeIndex} of slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_delete_slide',
            description: 'Delete a slide from the presentation by index',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide to delete',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                slide.delete();
                await context.sync();
            });
            return {
                content: [{ type: 'text', text: `Deleted slide ${slideIndex}` }],
            };
        },
    },
    // -----------------------------------------------------------------------
    // NEW Tier 2: Richer content manipulation
    // -----------------------------------------------------------------------
    {
        tool: {
            name: 'powerpoint_insert_text_box',
            description: 'Insert a new text box on a slide with specified position and size',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    text: {
                        type: 'string',
                        description: 'The text content for the text box',
                    },
                    left: {
                        type: 'number',
                        description: 'Left position in points (default: 50)',
                    },
                    top: {
                        type: 'number',
                        description: 'Top position in points (default: 50)',
                    },
                    width: {
                        type: 'number',
                        description: 'Width in points (default: 400)',
                    },
                    height: {
                        type: 'number',
                        description: 'Height in points (default: 100)',
                    },
                },
                required: ['slide_index', 'text'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const text = args.text;
            const left = args.left ?? 50;
            const top = args.top ?? 50;
            const width = args.width ?? 400;
            const height = args.height ?? 100;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const textBox = slide.shapes.addTextBox(text, {
                    left,
                    top,
                    width,
                    height,
                });
                textBox.name = `TextBox_${Date.now()}`;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted text box on slide ${slideIndex} at (${left}, ${top}) ${width}×${height}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_insert_image',
            description: 'Insert an image onto a slide from a Base64-encoded string',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    base64_image: {
                        type: 'string',
                        description: 'Base64-encoded image data (without the data:image/… prefix)',
                    },
                    left: {
                        type: 'number',
                        description: 'Left position in points (default: 50)',
                    },
                    top: {
                        type: 'number',
                        description: 'Top position in points (default: 50)',
                    },
                    width: {
                        type: 'number',
                        description: 'Width in points (default: 400)',
                    },
                    height: {
                        type: 'number',
                        description: 'Height in points (default: 300)',
                    },
                },
                required: ['slide_index', 'base64_image'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const base64Image = args.base64_image;
            const left = args.left ?? 50;
            const top = args.top ?? 50;
            const width = args.width ?? 400;
            const height = args.height ?? 300;
            const MAX_BASE64_LENGTH = 10_485_760; // ~7.5 MB decoded
            if (base64Image.length > MAX_BASE64_LENGTH) {
                const sizeMB = ((base64Image.length * 3) / 4 / 1_048_576).toFixed(1);
                throw new Error(`Image too large (${sizeMB} MB). Maximum allowed is ~7.5 MB.`);
            }
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                slide.shapes.addImage(base64Image, {
                    left,
                    top,
                    width,
                    height,
                });
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted image on slide ${slideIndex} at (${left}, ${top}) ${width}×${height}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_set_speaker_notes',
            description: 'Set the speaker notes for a specific slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    notes: {
                        type: 'string',
                        description: 'The speaker notes text to set',
                    },
                },
                required: ['slide_index', 'notes'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const notes = args.notes;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const slideAny = slide;
                const notesSlide = slideAny.notesSlide;
                if (!notesSlide) {
                    throw new Error(`Slide ${slideIndex} does not have a notes slide`);
                }
                const shapes = notesSlide.shapes;
                shapes.load('items,type');
                await context.sync();
                // The notes slide has a slide image placeholder and a notes body.
                // The notes body is typically type "Body" (placeholder index 2) or
                // the second text-capable shape. We try to identify it by type first,
                // then fall back to the second writable text frame.
                const writableFrames = [];
                for (const shape of shapes.items) {
                    try {
                        const tf = shape.textFrame;
                        tf.load('hasText');
                        writableFrames.push({ tf, shapeType: shape.type });
                    }
                    catch {
                        // skip non-text shapes
                    }
                }
                await context.sync();
                // Prefer the "Body" placeholder; otherwise use the second text frame
                const bodyFrame = writableFrames.find((f) => f.shapeType === 'Body') ??
                    writableFrames[1] ??
                    writableFrames[0];
                if (!bodyFrame) {
                    throw new Error('Could not find notes text frame');
                }
                bodyFrame.tf.textRange.text = notes;
                await context.sync();
            });
            return {
                content: [{ type: 'text', text: `Set speaker notes on slide ${slideIndex}` }],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_insert_table',
            description: 'Insert a table on a slide (requires PowerPointApi 1.8+)',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    rows: {
                        type: 'array',
                        items: {
                            type: 'array',
                            items: { type: 'string' },
                        },
                        description: 'A 2D array of cell values (rows × columns)',
                    },
                    left: {
                        type: 'number',
                        description: 'Left position in points (default: 50)',
                    },
                    top: {
                        type: 'number',
                        description: 'Top position in points (default: 100)',
                    },
                    width: {
                        type: 'number',
                        description: 'Width in points (default: 600)',
                    },
                    height: {
                        type: 'number',
                        description: 'Height in points (default: 300)',
                    },
                },
                required: ['slide_index', 'rows'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const rows = args.rows;
            const left = args.left ?? 50;
            const top = args.top ?? 100;
            const width = args.width ?? 600;
            const height = args.height ?? 300;
            if (!rows.length || !rows[0].length) {
                return { content: [{ type: 'text', text: 'Error: rows array must not be empty' }] };
            }
            const rowCount = rows.length;
            const colCount = rows[0].length;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const table = slide.shapes.addTable(rowCount, colCount, {
                    left,
                    top,
                    width,
                    height,
                });
                table.load('rows/items/cells/items/textFrame/textRange');
                await context.sync();
                // Populate cell values row by row
                for (let r = 0; r < rowCount; r++) {
                    const row = table.rows.items[r];
                    for (let c = 0; c < colCount; c++) {
                        if (rows[r][c] !== undefined) {
                            row.cells.items[c].textFrame.textRange.text = rows[r][c];
                        }
                    }
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted ${rowCount}×${colCount} table on slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_move_shape',
            description: 'Move and/or resize a shape on a slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    shape_index: {
                        type: 'number',
                        description: 'The 0-based index of the shape on the slide',
                    },
                    left: { type: 'number', description: 'New left position in points' },
                    top: { type: 'number', description: 'New top position in points' },
                    width: { type: 'number', description: 'New width in points' },
                    height: { type: 'number', description: 'New height in points' },
                },
                required: ['slide_index', 'shape_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const shapeIndex = args.shape_index;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                if (shapeIndex < 0 || shapeIndex >= shapes.items.length) {
                    throw new Error(`Shape index ${shapeIndex} out of range. Slide has ${shapes.items.length} shape(s).`);
                }
                const shape = shapes.items[shapeIndex];
                if (args.left !== undefined)
                    shape.left = args.left;
                if (args.top !== undefined)
                    shape.top = args.top;
                if (args.width !== undefined)
                    shape.width = args.width;
                if (args.height !== undefined)
                    shape.height = args.height;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Updated shape ${shapeIndex} on slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    // -----------------------------------------------------------------------
    // NEW Tier 3: Advanced capabilities
    // -----------------------------------------------------------------------
    {
        tool: {
            name: 'powerpoint_add_slide_from_layout',
            description: 'Add a new slide using a specific slide master and layout. Use powerpoint_get_slide_layouts to discover available layouts.',
            inputSchema: {
                type: 'object',
                properties: {
                    master_id: {
                        type: 'string',
                        description: 'The ID of the slide master',
                    },
                    layout_id: {
                        type: 'string',
                        description: 'The ID of the slide layout',
                    },
                },
                required: ['layout_id'],
            },
        },
        execute: async (args) => {
            const layoutId = args.layout_id;
            const masterId = args.master_id;
            const slideNumber = await PowerPoint.run(async (context) => {
                const slides = context.presentation.slides;
                const options = { layoutId };
                if (masterId)
                    options.masterId = masterId;
                slides.add(options);
                slides.load('items');
                await context.sync();
                return slides.items.length;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Added slide ${slideNumber} from layout ${layoutId}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_slide_layouts',
            description: 'Get available slide masters and their layouts for use with powerpoint_add_slide_from_layout',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const result = await PowerPoint.run(async (context) => {
                const masters = context.presentation.slideMasters;
                masters.load('items,id,name');
                await context.sync();
                const output = [];
                for (const master of masters.items) {
                    const layouts = master.layouts;
                    layouts.load('items,id,name');
                    await context.sync();
                    output.push({
                        masterId: master.id,
                        masterName: master.name,
                        layouts: layouts.items.map((l) => ({
                            layoutId: l.id,
                            layoutName: l.name,
                        })),
                    });
                }
                return output;
            });
            return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
        },
    },
    {
        tool: {
            name: 'powerpoint_export_slide_image',
            description: 'Export a slide as a Base64-encoded image for visual analysis (requires PowerPointApi 1.5+)',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const result = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const slideAny = slide;
                if (typeof slideAny.exportAsImage !== 'function') {
                    throw new Error('Slide image export not supported in this version of PowerPoint. Try using powerpoint_get_slide_shapes for structural analysis instead.');
                }
                const image = slideAny.exportAsImage();
                await context.sync();
                return image.value;
            });
            return {
                content: [
                    {
                        type: 'image',
                        data: result,
                        mimeType: 'image/png',
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_get_hyperlinks',
            description: 'Get all hyperlinks from shapes on a specific slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const result = await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items,name');
                await context.sync();
                const hyperlinks = [];
                for (let i = 0; i < shapes.items.length; i++) {
                    const shape = shapes.items[i];
                    try {
                        const tf = shape.textFrame;
                        tf.load('hasText');
                        await context.sync();
                        if (tf.hasText) {
                            const tr = tf.textRange;
                            tr.load('text,hyperlink');
                            await context.sync();
                            const trAny = tr;
                            if (trAny.hyperlink?.address) {
                                hyperlinks.push({
                                    shapeIndex: i,
                                    shapeName: shape.name,
                                    url: trAny.hyperlink.address,
                                    text: tr.text ?? '',
                                });
                            }
                        }
                    }
                    catch {
                        // skip shapes without text frames
                    }
                }
                return hyperlinks;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: result.length > 0
                            ? JSON.stringify(result, null, 2)
                            : `No hyperlinks found on slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_set_shape_fill',
            description: 'Set the fill color of a shape on a slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide',
                    },
                    shape_index: {
                        type: 'number',
                        description: 'The 0-based index of the shape on the slide',
                    },
                    color: {
                        type: 'string',
                        description: 'Fill color as hex (e.g. "#FF0000") or named color',
                    },
                    transparency: {
                        type: 'number',
                        description: 'Fill transparency from 0 (opaque) to 1 (fully transparent)',
                    },
                },
                required: ['slide_index', 'shape_index', 'color'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            const shapeIndex = args.shape_index;
            const color = args.color;
            const transparency = args.transparency;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                if (shapeIndex < 0 || shapeIndex >= shapes.items.length) {
                    throw new Error(`Shape index ${shapeIndex} out of range. Slide has ${shapes.items.length} shape(s).`);
                }
                const shape = shapes.items[shapeIndex];
                const fill = shape.fill;
                fill.setSolidColor(color);
                if (transparency !== undefined) {
                    ;
                    fill.transparency = transparency;
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set fill color ${color} on shape ${shapeIndex} of slide ${slideIndex}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'powerpoint_navigate_to_slide',
            description: 'Navigate the user to a specific slide',
            inputSchema: {
                type: 'object',
                properties: {
                    slide_index: {
                        type: 'number',
                        description: 'The 1-based index of the slide to navigate to',
                    },
                },
                required: ['slide_index'],
            },
        },
        execute: async (args) => {
            const slideIndex = args.slide_index;
            await PowerPoint.run(async (context) => {
                const slide = await getSlideByIndex(context, slideIndex);
                slide.load('id');
                await context.sync();
                context.presentation.setSelectedSlides([slide.id]);
                await context.sync();
            });
            return {
                content: [{ type: 'text', text: `Navigated to slide ${slideIndex}` }],
            };
        },
    },
];
